import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useTelephony } from "../../context/TelephonyContext";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { telephonyService, type CallLogDto } from "../../services/telephonyService";
import { vorgangService } from "../../services/vorgangService";
import { I } from "../redesign/kvpIcons";
import AssignNumberDialog from "./AssignNumberDialog";
import CallVorgangZuweisenDialog from "./CallVorgangZuweisenDialog";
import { openVorgangFromCall } from "./callVorgangFlow";
import { serverMeldung, weiseZu } from "./callVorgangZuweisen";
import { hatZuordnung, vorgangIdsVon, zuordnungLabel, zuordnungenVon } from "./callZuordnungen";
import { KontaktInVorgangAufnehmenHost } from "./KontaktInVorgangAufnehmenPopup";
import { VorgangTodoAuswahl } from "../vorgang/picker/VorgangTodoAuswahl";
import { anrufZeileLaden, gespraechsDauer } from "./nachAnrufRegel";
import { anrufAnzeigeName, anrufAnzeigeNummer, istInterneNummer } from "./telephonyUtils";
import { softphone } from "../../services/linkusSdk";
import "../redesign/contactRedesign.css";

/**
 * NachAnrufKarte — nach dem Auflegen wird aus dem Anruf-Popup diese Karte
 * (P2): an derselben Stelle (Viewport-Mitte), in derselben Optik (`.callw-*`).
 * Kontakt und Vorgang werden hier in einem Rutsch erledigt. Die Notiz steht
 * nicht mehr hier: Sie wird im Gespräch im Anruf-Popup getippt und beim
 * Auflegen direkt gespeichert (T-13).
 *
 * Ob sie erscheint, entscheidet der TelephonyProvider (`nachAnruf`, Regel in
 * `nachAnrufRegel.ts`). Sie ersetzt die frühere Nachfrage `NachAnrufZuordnen`
 * — sonst stünden nach einem Gespräch mit unbekannter Nummer zwei Fenster da.
 *
 * Gemountet in der AppShell INNERHALB des WindowManagers (nicht neben dem
 * CallWidget): „Kontakt anlegen" öffnet die volle Kontaktanlage und „Neuer
 * Vorgang" den Erstell-Flow als Fenster.
 *
 * Vorrang hat immer ein laufendes Gespräch: Solange das Anruf-Popup offen ist,
 * schrumpft die Karte zur Leiste „Nachbearbeitung offen".
 */

const ico = (d: React.ReactNode, size = 16): React.ReactElement => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor"
        strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
        {d}
    </svg>
);
const PersonIcon = ico(<><circle cx="10" cy="7" r="3.2" /><path d="M4 17c.8-3.2 3.2-5 6-5s5.2 1.8 6 5" /></>);
const TaskIcon = ico(<><rect x="4" y="3.5" width="12" height="13" rx="2" /><path d="M7 8l1.5 1.5L11 7M7 13h6" /></>);
const ExpandIcon = ico(<path d="M6 12l4-4 4 4" />);

type Unterdialog = "kontakt" | "kontaktFuerVorgang" | "zuweisen" | null;

const vorgangLabel = (v: { id: number; titel?: string | null; vorgangNbr?: string | null }): string =>
    `${v.vorgangNbr || `#${v.id}`}${v.titel ? ` · ${v.titel}` : ""}`;

/**
 * Was der Karte als Vorgang vorgeschlagen wird: der Vorgang, aus dem gewählt
 * wurde (Wählkontext, hat Vorrang), sonst — nur ohne jede Zuordnung — der
 * eindeutig passende aktive Vorgang des Kontakts vom Server (F3: A14 ordnet
 * an einen Vorgang MIT To-dos nicht selbst zu, sondern schlägt ihn hier vor).
 */
type KartenVorschlag = {
    vorgangId: number;
    titel: string;
    /** Aus einem To-do heraus gewählt → direkt dieses To-do. */
    todoId: number | null;
    /** To-dos des Vorgangs; `null` = werden geladen. */
    todos: Array<{ id: number; titel: string | null; status?: string | null }> | null;
};

const NachAnrufKarte: React.FC = () => (
    <>
        {/* F2-Rückfrage „Kontakt mit aufnehmen?" — app-weit, für jeden Weg, der
            einen Anruf einem Vorgang zuordnet (Bote in kontaktInVorgangAbgleich). */}
        <KontaktInVorgangAufnehmenHost />
        <NachAnrufKarteInhalt />
    </>
);

const NachAnrufKarteInhalt: React.FC = () => {
    const { nachAnruf, nachAnrufSchliessen, activeCall, minimized, minimize } = useTelephony();
    const { openWindow } = useWindowManager();

    const [call, setCall] = useState<CallLogDto | null>(null);
    const [laedt, setLaedt] = useState(false);
    const [kontaktName, setKontaktName] = useState<string | null>(null);
    const [unterdialog, setUnterdialog] = useState<Unterdialog>(null);
    const [leiste, setLeiste] = useState(false);
    const [vorschlag, setVorschlag] = useState<KartenVorschlag | null>(null);
    const [fehler, setFehler] = useState("");
    const [arbeitet, setArbeitet] = useState(false);

    const callId = nachAnruf?.callId ?? null;

    // Neues Gespräch → Karte frisch aufsetzen und die Zeile laden (falls der
    // Provider sie nicht schon mitgebracht hat).
    useEffect(() => {
        setUnterdialog(null);
        setLeiste(false);
        setFehler("");
        setVorschlag(null);
        setKontaktName(nachAnruf?.kontaktName ?? null);
        setCall(nachAnruf?.call ?? null);
        if (!nachAnruf || nachAnruf.call) return;
        let abgebrochen = false;
        const laden = () =>
            anrufZeileLaden(nachAnruf.callId, {
                callLogId: softphone.callLogIdFor(nachAnruf.callId),
                suchen: (id) =>
                    telephonyService
                        .listCalls({ callId: id, limit: 1, mitZuordnungen: true })
                        .then((r) => r.calls[0] ?? null),
            });
        setLaedt(true);
        void (async () => {
            let zeile = await laden();
            // Scheiterte der Ingest, legt der CDR-Abgleich die Zeile meist
            // binnen Sekunden an — einmal nachfassen.
            if (!zeile && !abgebrochen) {
                await new Promise((r) => window.setTimeout(r, 15_000));
                if (!abgebrochen) zeile = await laden();
            }
            if (abgebrochen) return;
            setCall(zeile);
            setLaedt(false);
        })();
        return () => {
            abgebrochen = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim Gesprächswechsel
    }, [callId]);

    // Kontaktname aus der Zeile nachziehen (Zuordnung im Dialog, CDR).
    useEffect(() => {
        if (call?.contactDisplayName) setKontaktName(call.contactDisplayName);
    }, [call?.contactDisplayName]);

    // Vorschlag 1: der Wählkontext (aus einem Vorgang/To-do heraus gewählt).
    const wahl = nachAnruf?.vorschlag ?? null;
    const wahlVorgangId = wahl?.vorgangId ?? null;
    const wahlTodoId = wahl?.todoId ?? null;
    useEffect(() => {
        if (wahlVorgangId == null) return;
        let ab = false;
        setVorschlag({ vorgangId: wahlVorgangId, titel: `#${wahlVorgangId}`, todoId: wahlTodoId, todos: null });
        vorgangService
            .getVorgang(wahlVorgangId)
            .then((v) => !ab && setVorschlag((p) => (p && p.vorgangId === wahlVorgangId ? { ...p, titel: vorgangLabel(v) } : p)))
            .catch(() => undefined);
        // Ohne To-do im Wählkontext: die To-dos des Vorgangs — hat er welche,
        // wird angehakt statt blind an den Vorgang gehängt (V4-Invariante).
        (wahlTodoId != null ? Promise.resolve([]) : vorgangService.pickerTodos(wahlVorgangId))
            .then((todos) => !ab && setVorschlag((p) => (p && p.vorgangId === wahlVorgangId ? { ...p, todos } : p)))
            .catch(() => !ab && setVorschlag((p) => (p && p.vorgangId === wahlVorgangId ? { ...p, todos: [] } : p)));
        return () => {
            ab = true;
        };
    }, [wahlVorgangId, wahlTodoId]);

    // Vorschlag 2 (F3): nur ohne Wählkontext und ohne jede Zuordnung.
    const ohneZuordnung = !!call && !hatZuordnung(call);
    const callLogId = call?.id ?? null;
    useEffect(() => {
        if (wahlVorgangId != null || !ohneZuordnung || callLogId == null) return;
        let ab = false;
        telephonyService
            .vorgangVorschlag(callLogId)
            .then((r) => {
                if (ab || !r.vorgang) return;
                setVorschlag({
                    vorgangId: r.vorgang.id,
                    titel: vorgangLabel(r.vorgang),
                    todoId: null,
                    todos: r.vorgang.todos,
                });
            })
            .catch(() => undefined);
        return () => {
            ab = true;
        };
    }, [wahlVorgangId, ohneZuordnung, callLogId]);

    const hatKontakt = call?.contactId != null || !!kontaktName;

    const fertig = useCallback(() => nachAnrufSchliessen(), [nachAnrufSchliessen]);
    const spaeter = useCallback(
        () => nachAnrufSchliessen({ kontaktSpaeter: !hatKontakt }),
        [nachAnrufSchliessen, hatKontakt],
    );

    const neuerVorgang = useCallback(() => {
        if (!call) return;
        if (call.contactId == null) {
            // Ein neuer Vorgang braucht einen Kunden — erst zuordnen (wie in der Liste).
            setUnterdialog("kontaktFuerVorgang");
            return;
        }
        // Der Erstell-Flow ist ein Fenster; die Karte darf es nicht verdecken.
        setLeiste(true);
        openVorgangFromCall(openWindow, call, (next) => {
            setCall(next);
            setLeiste(false);
        });
    }, [call, openWindow]);

    /** Zuordnen fügt HINZU (PO 01.10.2026) — bestehende Zuordnungen bleiben. */
    const zuordnen = useCallback(
        async (ziele: Array<{ vorgangId: number; todoId: number | null }>) => {
            if (!call) return;
            setArbeitet(true);
            setFehler("");
            try {
                setCall(await weiseZu(call, ziele));
            } catch (e) {
                setFehler(serverMeldung(e) ?? "Zuordnung fehlgeschlagen. Bitte erneut versuchen.");
                throw e;
            } finally {
                setArbeitet(false);
            }
        },
        [call],
    );

    if (!nachAnruf) return null;

    const nummer = nachAnruf.number;
    const namensQuelle = { number: nummer, pbxName: nachAnruf.pbxName, kontaktName };
    const anzeigeName = anrufAnzeigeName(namensQuelle);
    const unterzeile = anrufAnzeigeNummer(namensQuelle);
    const intern = istInterneNummer(nummer);

    // Unterdialoge liegen über allem — die Karte macht solange Platz.
    if (unterdialog === "kontakt" || unterdialog === "kontaktFuerVorgang") {
        const fuerVorgang = unterdialog === "kontaktFuerVorgang";
        return (
            <AssignNumberDialog
                call={call}
                number={nummer}
                purpose={fuerVorgang ? "vorgang" : "nachAnruf"}
                onClose={() => {
                    // Abbrechen ODER Wechsel in die volle Kontaktanlage (ein
                    // Fenster) — in beiden Fällen nicht darüberlegen.
                    setUnterdialog(null);
                    setLeiste(true);
                }}
                onAssigned={(updated) => {
                    setUnterdialog(null);
                    setLeiste(false);
                    if (updated) setCall(updated);
                    else if (nummer)
                        void telephonyService
                            .resolveOwners(nummer)
                            .then((r) => r?.displayName && setKontaktName(r.displayName));
                    if (fuerVorgang && updated?.contactId != null) {
                        setLeiste(true);
                        openVorgangFromCall(openWindow, updated, (next) => {
                            setCall(next);
                            setLeiste(false);
                        });
                    }
                }}
            />
        );
    }
    if (unterdialog === "zuweisen" && call) {
        return (
            <CallVorgangZuweisenDialog
                call={call}
                onClose={() => setUnterdialog(null)}
                onAssigned={(next) => {
                    setCall(next);
                    setUnterdialog(null);
                }}
            />
        );
    }

    // Laufendes Gespräch hat Vorrang (Popup mittig) — oder die Karte macht
    // einem Fenster Platz: dann nur die Leiste.
    const popupOffen = !!activeCall && !minimized;
    if (leiste || popupOffen) {
        return createPortal(
            <div className="kvp-rd callw-nachleiste" role="status" aria-label="Nachbearbeitung offen">
                <span className="callw-nachleiste-text" title={anzeigeName}>
                    Nachbearbeitung offen · {anzeigeName}
                </span>
                <button
                    type="button"
                    className="callw-nachleiste-btn"
                    onClick={() => {
                        if (popupOffen) minimize();
                        setLeiste(false);
                    }}
                >
                    {ExpandIcon} Öffnen
                </button>
            </div>,
            document.body,
        );
    }

    const initialen = kontaktName
        ? kontaktName
              .split(/[\s,]+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((t) => t[0])
              .join("")
              .toUpperCase()
        : null;

    return createPortal(
        <div className="kvp-rd callw-root" role="dialog" aria-label="Gespräch nachbereiten">
            <div className="callw-card">
                <div className="callw-head">
                    <div className="callw-avatar" aria-hidden>
                        {initialen || I.phone}
                    </div>
                    <div className="callw-id">
                        <div className="callw-name" title={anzeigeName}>{anzeigeName}</div>
                        {unterzeile && <div className="callw-sub">{unterzeile}</div>}
                    </div>
                </div>

                <div className="callw-rule" />

                <div className="callw-body">
                    <div className="callw-state">
                        <span className="callw-timer">{gespraechsDauer(nachAnruf.dauerSec)}</span>
                        <span className="callw-statelabel">Gespräch beendet</span>
                    </div>

                    {!intern && (
                        <div className="callw-sec">
                            <div className="callw-sec-k">{PersonIcon} Kontakt</div>
                            {hatKontakt ? (
                                <div className="callw-sec-v" title={kontaktName ?? undefined}>
                                    {kontaktName ?? "zugeordnet"}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="callw-btn"
                                    onClick={() => setUnterdialog("kontakt")}
                                >
                                    Kontakt zuordnen / anlegen
                                </button>
                            )}
                        </div>
                    )}

                    <div className="callw-sec">
                        <div className="callw-sec-k">{TaskIcon} Vorgang</div>
                        {call && hatZuordnung(call) ? (
                            <div className="callw-zlist">
                                {zuordnungenVon(call).map((z) => (
                                    <div
                                        key={z.id ?? `s${z.vorgangId}`}
                                        className="callw-sec-v"
                                        title={zuordnungLabel(z)}
                                    >
                                        {zuordnungLabel(z)}
                                    </div>
                                ))}
                                {(call.weitereZuordnungenVerborgen ?? 0) > 0 && (
                                    <div className="callw-thint">
                                        + {call.weitereZuordnungenVerborgen} weitere (keine Berechtigung)
                                    </div>
                                )}
                                <div className="callw-sec-row">
                                    <button
                                        type="button"
                                        className="callw-tool callw-tool-klein"
                                        onClick={() => setUnterdialog("zuweisen")}
                                    >
                                        + weiterer Vorgang
                                    </button>
                                    <button
                                        type="button"
                                        className="callw-tool callw-tool-klein"
                                        onClick={neuerVorgang}
                                    >
                                        Neuer Vorgang (zusätzlich)
                                    </button>
                                </div>
                            </div>
                        ) : !call ? (
                            <div className="callw-thint">
                                {laedt
                                    ? "Anruf wird geladen …"
                                    : "Der Anruf ist noch nicht im Verlauf angekommen. Den Vorgang bitte später in der Anrufliste zuordnen."}
                            </div>
                        ) : (
                            <div className="callw-sec-btns">
                                {vorschlag && (
                                    <VorschlagZeile
                                        vorschlag={vorschlag}
                                        auch={false}
                                        arbeitet={arbeitet}
                                        onZuordnen={zuordnen}
                                    />
                                )}
                                <div className="callw-actions">
                                    <button type="button" className="callw-btn" onClick={neuerVorgang}>
                                        Neuer Vorgang
                                    </button>
                                    <button
                                        type="button"
                                        className="callw-btn"
                                        onClick={() => setUnterdialog("zuweisen")}
                                    >
                                        Bestehendem zuweisen
                                    </button>
                                </div>
                            </div>
                        )}
                        {/* Hängt woanders, aber nicht am Vorgang, aus dem gewählt wurde:
                            ZUSÄTZLICH dorthin (nicht „stattdessen" — ein Anruf darf an
                            mehreren Vorgängen hängen). */}
                        {call && hatZuordnung(call) && vorschlag && wahlVorgangId != null &&
                            !vorgangIdsVon(call).includes(wahlVorgangId) && (
                                <VorschlagZeile
                                    vorschlag={vorschlag}
                                    auch
                                    arbeitet={arbeitet}
                                    onZuordnen={zuordnen}
                                />
                            )}
                        {fehler && <div className="callw-fehler">{fehler}</div>}
                    </div>

                    <div className="callw-foot">
                        <button type="button" className="callw-tool" onClick={spaeter}>
                            Später
                        </button>
                        <button type="button" className="callw-btn ok" onClick={fertig}>
                            Fertig
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

/**
 * Ein vorgeschlagener Vorgang: ohne To-dos (oder mit gewähltem To-do) ein
 * Knopf „Zu …"; MIT To-dos die aufklappbare Zeile zum Anhaken (F3).
 */
const VorschlagZeile: React.FC<{
    vorschlag: KartenVorschlag;
    auch: boolean;
    arbeitet: boolean;
    onZuordnen: (ziele: Array<{ vorgangId: number; todoId: number | null }>) => Promise<void>;
}> = ({ vorschlag, auch, arbeitet, onZuordnen }) => {
    const praefix = auch ? "Auch zu" : "Zu";
    if (vorschlag.todoId == null && vorschlag.todos == null) {
        return (
            <button type="button" className="callw-btn on" disabled>
                {praefix} {vorschlag.titel} …
            </button>
        );
    }
    if (vorschlag.todoId == null && (vorschlag.todos?.length ?? 0) > 0) {
        return (
            <VorgangTodoAuswahl
                vorgangId={vorschlag.vorgangId}
                titel={`${praefix} ${vorschlag.titel}`}
                todos={vorschlag.todos ?? []}
                disabled={arbeitet}
                onZuordnen={(ziele) => onZuordnen(ziele)}
            />
        );
    }
    return (
        <button
            type="button"
            className={auch ? "callw-tool callw-tool-klein" : "callw-btn on"}
            onClick={() =>
                void onZuordnen([{ vorgangId: vorschlag.vorgangId, todoId: vorschlag.todoId }]).catch(() => undefined)
            }
            disabled={arbeitet}
            title={auch ? "Zusätzlich dem Vorgang zuordnen, aus dem gewählt wurde" : "Der vorgeschlagene Vorgang"}
        >
            {praefix} {vorschlag.titel}
        </button>
    );
};

export default NachAnrufKarte;
